import Badge from './Badge';

/**
 * Tarjeta-botón compleja del Home: icon + label + descripción + badge
 * opcional. Layout interno fijo. Cada card linkea a un módulo del producto.
 *
 * Identidad de módulo SOLO en el icon (text-module-X). El resto del card
 * se mantiene neutro. Excepción: variant="featured" tinta el fondo con
 * accent-bg (porque accent = brand = módulo PAE per Decisión 0.1 del
 * tokens doc).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon
 * @param {string} props.label
 * @param {string} [props.description]
 * @param {'pae'|'prepara-internado'|'prepara-turno'|'revisa'|'bitacora'|'progreso'|null} [props.module=null]
 * @param {'default'|'featured'} [props.variant='default']
 * @param {string|React.ReactNode} [props.badge]   Texto del badge o nodo custom.
 * @param {'default'|'info'|'success'|'module'} [props.badgeTone]
 *   Default depende: si module presente → 'module', sino 'default'.
 * @param {boolean} [props.disabled=false]
 * @param {'fila'|'mosaico'} [props.layout='fila']  'mosaico' apila icono,
 *   nombre y una descripción corta para la grilla de dos columnas de los
 *   atajos del inicio (2026-10-16). Mismo marco y mismos colores.
 * @param {() => void} [props.onClick]
 */
export default function HomeCard({
  icon,
  label,
  description,
  module = null,
  variant = 'default',
  badge,
  badgeTone,
  disabled = false,
  layout = 'fila',
  onClick,
}) {
  const isFeatured = variant === 'featured';
  const mosaico = layout === 'mosaico';
  const isInteractive = !!onClick && !disabled;

  // Card frame por variant
  const frameClass = isFeatured
    ? 'bg-accent-bg border border-accent-border'
    : 'border border-edge';

  const moduleBg = !isFeatured && module ? MODULE_BG_VARS[module] : null;

  // Hover/active solo si interactivo
  const interactiveClass = !isInteractive
    ? ''
    : isFeatured
    ? 'hover:opacity-90 active:scale-[0.99]'
    : 'hover:opacity-95 active:scale-[0.99]';

  // Icon color: identidad de módulo (excepto featured = accent del brand)
  const iconColorClass = isFeatured
    ? 'text-accent'
    : module
    ? MODULE_ICON_CLASSES[module] || 'text-ink-muted'
    : 'text-ink-muted';

  // Texto: en featured va en accent; en default neutro.
  const labelColorClass = isFeatured ? 'text-accent font-semibold' : 'text-ink font-semibold';
  const descColorClass = isFeatured ? 'text-accent' : 'text-ink-muted';

  // Badge: si disabled y no se pasa badge, inyectamos "Pronto" automático.
  const resolvedBadge = badge || (disabled ? 'Pronto' : null);
  const resolvedBadgeTone = badgeTone || (module ? 'module' : 'default');

  const focusRing = isInteractive
    ? 'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper'
    : '';

  const Tag = isInteractive ? 'button' : 'div';
  const tagProps = isInteractive
    ? { type: 'button', onClick, disabled }
    : {};

  return (
    <Tag
      {...tagProps}
      style={moduleBg ? { background: moduleBg } : undefined}
      className={[
        mosaico
          ? 'relative w-full h-full min-h-[88px] flex flex-col items-start gap-2 p-3.5 rounded-tk-md text-left'
          : 'relative w-full flex items-start gap-4 p-4 rounded-tk-md text-left',
        'transition-all duration-150',
        frameClass,
        interactiveClass,
        focusRing,
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        !isFeatured && !moduleBg ? 'bg-paper' : '',
      ].join(' ')}
    >
      <span className={`flex-shrink-0 ${iconColorClass}`} aria-hidden="true">
        {icon}
      </span>
      <span className={mosaico ? 'min-w-0' : 'flex-1 min-w-0 pr-2'}>
        <span className={`block text-body ${mosaico ? 'leading-tight ' : ''}${labelColorClass}`}>{label}</span>
        {description && (
          <span className={`block text-small ${descColorClass} mt-0.5 ${mosaico ? 'leading-snug' : 'leading-relaxed'}`}>
            {description}
          </span>
        )}
      </span>
      {resolvedBadge && (
        <span className="absolute top-3 right-3">
          {typeof resolvedBadge === 'string' ? (
            <Badge tone={resolvedBadgeTone} module={module || undefined}>
              {resolvedBadge}
            </Badge>
          ) : (
            resolvedBadge
          )}
        </span>
      )}
    </Tag>
  );
}

// DEUDA TÉCNICA — este mapping está duplicado en:
//   - foundational/ModuleHeader.jsx (MODULE_TOKENS)
//   - foundational/HomeCard.jsx (MODULE_ICON_CLASSES + MODULE_BG_VARS)
//   - foundational/Badge.jsx (MODULE_CLASSES)
//   - foundational/MenuItem.jsx (MODULE_ICON_CLASSES)
// Cuando agregués un módulo nuevo: actualizar las 4 entradas.
// Refactor a una fuente única de verdad pendiente (post-stores).
const MODULE_ICON_CLASSES = {
  pae:                 'text-module-pae',
  'pae-rapido':        'text-module-pae-rapido',
  'pae-detallado':     'text-module-pae-detallado',
  'prepara-practica':  'text-module-prepara-practica',
  'prepara-internado': 'text-module-prepara-internado',
  'prepara-turno':     'text-module-prepara-turno',
  revisa:              'text-module-revisa',
  bitacora:            'text-module-bitacora',
  calendario:          'text-module-calendario',
  progreso:            'text-module-progreso',
  escalas:             'text-module-escalas',
  medicamentos:        'text-module-medicamentos',
  respira:             'text-module-respira',
  bienestar:           'text-module-bienestar',
  enciclopedia:        'text-module-enciclopedia',
  bibliografia:        'text-module-bibliografia',
  ges:                 'text-module-ges',
};

const MODULE_BG_VARS = {
  'pae-rapido':        'var(--module-pae-rapido-bg)',
  'pae-detallado':     'var(--module-pae-detallado-bg)',
  'prepara-practica':  'var(--module-prepara-practica-bg)',
  'prepara-internado': 'var(--module-prepara-internado-bg)',
  'prepara-turno':     'var(--module-prepara-turno-bg)',
  revisa:              'var(--module-revisa-bg)',
  bitacora:            'var(--module-bitacora-bg)',
  calendario:          'var(--module-calendario-bg)',
  escalas:             'var(--module-escalas-bg)',
  medicamentos:        'var(--module-medicamentos-bg)',
  respira:             'var(--module-respira-bg)',
  bienestar:           'var(--module-bienestar-bg)',
  enciclopedia:        'var(--module-enciclopedia-bg)',
  bibliografia:        'var(--module-bibliografia-bg)',
  ges:                 'var(--module-ges-bg)',
};
